import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Navbar from '../components/Navbar';
import { TYPOGRAPHY, PLACEHOLDER_IMAGES } from '../constants/typography';

gsap.registerPlugin(ScrollTrigger);

const SegmentsPage = () => {
    const location = useLocation();
    // Comprehensive segments data
    const segments = [
        {
            number: "01",
            title: "Specialized & Industrial Interiors",
            description: "This segment includes properties with highly specialized functional requirements, often involving specific equipment, environmental controls, or operational efficiency.",
            color: "#1a4d5c",
            gradient: "linear-gradient(135deg, #0f3443 0%, #1a4d5c 100%)",
            icon: "🏭",
            items: [
                "Warehouses & Industrial Units",
                "Research & Development Facilities",
                "Manufacturing Units & Production Facilities",
                "Data Centers & IT Facilities",
                "Logistics & Distribution Centers",
                "Spas & Wellness Centers",
                "Wellness Retreats (focusing on interior experience for specific services)",
                "Cruise Ships & Resorts (large-scale hospitality with complex, specialized design needs)"
            ]
        },
        {
            number: "02",
            title: "Institutional & Public Sector Interiors",
            description: "This category involves designing spaces for public services, education, healthcare, government, and large-scale community functions, often with specific regulations, accessibility, and high-traffic considerations.",
            color: "#2d1b2e",
            gradient: "linear-gradient(135deg, #2d1b2e 0%, #3d2b3e 100%)",
            icon: "🏛️",
            items: [
                "Hospitals & Healthcare Facilities",
                "Educational Institutions (Schools, Colleges)",
                "Airports & Transportation Hubs",
                "Government Buildings",
                "Banks & Financial Institutions",
                "Public Sector Projects",
                "Religious & Cultural Institutions",
                "Conference & Convention Centers",
                "Medical & Dental Clinics",
                "Community Centers",
                "Government Offices & Administrative Buildings",
                "Sports & Recreational Facilities",
                "Public Parks & Recreational Spaces (design of associated buildings/structures)"
            ]
        },
        {
            number: "03",
            title: "Commercial & Retail Interiors",
            description: "This segment focuses on spaces designed for business operations, sales, and customer interaction, emphasizing branding, workflow, and customer experience.",
            color: "#1a1a2e",
            gradient: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
            icon: "🏢",
            items: [
                "Offices & Corporate Offices",
                "Co-working Spaces",
                "Retail Showrooms",
                "Restaurants & Cafés",
                "Commercial Real Estate",
                "Retail Stores & Showrooms",
                "Retail Chains & Malls",
                "Showrooms (Automotive, Appliances)",
                "Shopping Complexes",
                "Law Firms & Legal Offices",
                "Corporate Headquarters",
                "Exhibition Halls",
                "Mobile Offices & Temporary Spaces",
                "Studios (Photography, Music, Art) - when designed for commercial use",
                "Cinemas & Entertainment Complexes",
                "Theme Parks & Entertainment Venues"
            ]
        },
        {
            number: "04",
            title: "Residential Interiors",
            description: "This category encompasses all spaces primarily designed for living, comfort, and personal expression. Interior services here focus on creating functional, aesthetically pleasing, and personalized homes.",
            color: "#2c3e50",
            gradient: "linear-gradient(135deg, #2c3e50 0%, #34495e 100%)",
            icon: "🏠",
            items: [
                "Farmhouses",
                "Villas & Luxury Villas",
                "Independent Houses",
                "Residential Construction",
                "Luxury Apartments",
                "High-end Residential Units",
                "Senior Living & Retirement Homes",
                "Agricultural Projects (Farmhouses, Greenhouses)",
                "Home Automation Systems (installation and integration within residential spaces)",
                "Libraries & Learning Centers (within a residential context, e.g., home libraries)",
                "Pet Care Facilities (home-based or small, personalized services)"
            ]
        }
    ];

    useEffect(() => {
        // Animate hero section
        gsap.fromTo('.segments-hero h1',
            { opacity: 0, y: 50 },
            { opacity: 1, y: 0, duration: 1, delay: 0.3, ease: 'power3.out' }
        );

        gsap.fromTo('.segments-hero p',
            { opacity: 0, y: 30 },
            { opacity: 1, y: 0, duration: 0.8, delay: 0.6, ease: 'power3.out' }
        );

        // Animate segment cards on scroll
        gsap.utils.toArray('.segment-detail-card').forEach((card, index) => {
            gsap.fromTo(card,
                { opacity: 0, y: 80, scale: 0.95 },
                {
                    opacity: 1,
                    y: 0,
                    scale: 1,
                    duration: 0.8,
                    delay: index * 0.1,
                    ease: 'power3.out',
                    scrollTrigger: {
                        trigger: card,
                        start: 'top 85%',
                        end: 'top 60%',
                        toggleActions: 'play none none reverse'
                    }
                }
            );
        });

        // Animate statistics
        gsap.fromTo('.stat-item',
            { opacity: 0, scale: 0.8 },
            {
                opacity: 1,
                scale: 1,
                duration: 0.6,
                stagger: 0.1,
                ease: 'back.out(1.7)',
                scrollTrigger: {
                    trigger: '.segments-stats',
                    start: 'top 75%'
                }
            }
        );

        return () => {
            ScrollTrigger.getAll().forEach(trigger => trigger.kill());
        };
    }, []);

    // Handle scrolling to specific segment from navigation
    useEffect(() => {
        if (location.state?.scrollToSegment) {
            const segmentId = location.state.scrollToSegment;
            setTimeout(() => {
                const element = document.getElementById(segmentId);
                if (element) {
                    const offsetTop = element.offsetTop - 120; // Increased offset for better visibility
                    window.scrollTo({
                        top: offsetTop,
                        behavior: 'smooth'
                    });
                }
            }, 300); // Increased delay to ensure page is fully loaded
        }
    }, [location]);

    return (
        <div>
            <Helmet>
                <title>Industry Segments - Hav'n Interiors</title>
                <meta name="description" content="Discover the diverse range of interior design segments we serve - from residential to commercial, institutional to specialized industrial spaces." />
                <meta name="keywords" content="interior design segments, commercial interiors, residential interiors, institutional design, industrial interiors" />
            </Helmet>

            <Navbar />

            {/* Hero Section */}
            <section className="segments-hero" style={{
                background: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url(${PLACEHOLDER_IMAGES.design})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundAttachment: 'fixed',
                padding: '160px 60px 100px',
                textAlign: 'center',
                position: 'relative',
                overflow: 'hidden'
            }}>
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'radial-gradient(circle at 50% 50%, rgba(201, 169, 110, 0.1) 0%, transparent 70%)',
                    pointerEvents: 'none'
                }}></div>
                
                <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
                    <h1 style={{
                        ...TYPOGRAPHY.h1,
                        marginBottom: '30px'
                    }}>
                        Industry Segments<br/>
                        <span style={{ color: 'var(--accent-color)' }}>We Serve</span>
                    </h1>
                    <p style={{
                        ...TYPOGRAPHY.bodyLarge,
                        maxWidth: '800px',
                        margin: '0 auto'
                    }}>
                        From residential homes to commercial spaces, institutional buildings to specialized industrial facilities,
                        we bring design excellence to every segment of the interior industry.
                    </p>
                </div>
            </section>

            {/* Statistics Section */}
            <section className="segments-stats" style={{
                background: '#0F0F0F',
                padding: '80px 60px',
                borderBottom: '1px solid rgba(201, 169, 110, 0.2)',
                display: 'flex',
                alignItems: 'center',
                minHeight: '280px'
            }}>
                <div style={{ maxWidth: '1400px', margin: '0 auto', width: '100%' }}>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                        gap: '40px',
                        textAlign: 'center'
                    }}>
                        <div className="stat-item">
                            <div style={{
                                fontSize: '64px',
                                fontWeight: 200,
                                color: 'var(--accent-color)',
                                marginBottom: '10px'
                            }}>4</div>
                            <div style={{
                                fontSize: '14px',
                                letterSpacing: '2px',
                                textTransform: 'uppercase',
                                color: 'rgba(245, 241, 237, 0.7)'
                            }}>Major Categories</div>
                        </div>

                        <div className="stat-item">
                            <div style={{
                                fontSize: '64px',
                                fontWeight: 200,
                                color: 'var(--accent-color)',
                                marginBottom: '10px'
                            }}>50+</div>
                            <div style={{
                                fontSize: '14px',
                                letterSpacing: '2px',
                                textTransform: 'uppercase',
                                color: 'rgba(245, 241, 237, 0.7)'
                            }}>Specializations</div>
                        </div>

                        <div className="stat-item">
                            <div style={{
                                fontSize: '64px',
                                fontWeight: 200,
                                color: 'var(--accent-color)',
                                marginBottom: '10px'
                            }}>8</div>
                            <div style={{
                                fontSize: '14px',
                                letterSpacing: '2px',
                                textTransform: 'uppercase',
                                color: 'rgba(245, 241, 237, 0.7)'
                            }}>Design Studios</div>
                        </div>

                        <div className="stat-item">
                            <div style={{
                                fontSize: '64px',
                                fontWeight: 200,
                                color: 'var(--accent-color)',
                                marginBottom: '10px'
                            }}>∞</div>
                            <div style={{
                                fontSize: '14px',
                                letterSpacing: '2px',
                                textTransform: 'uppercase',
                                color: 'rgba(245, 241, 237, 0.7)'
                            }}>Design Possibilities</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Segments Detail Section */}
            <section style={{
                background: '#0F0F0F',
                padding: '100px 60px',
                position: 'relative'
            }}>
                <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
                    {segments.map((segment, index) => (
                        <div key={segment.number} id={`segment-${segment.number}`} className="segment-detail-card" style={{
                            background: segment.gradient,
                            borderRadius: '24px',
                            padding: '60px',
                            marginBottom: '60px',
                            border: '1px solid rgba(201, 169, 110, 0.2)',
                            position: 'relative',
                            overflow: 'hidden'
                        }}>
                            {/* Background pattern */}
                            <div style={{
                                position: 'absolute',
                                top: 0,
                                right: 0,
                                fontSize: '200px',
                                opacity: 0.03,
                                fontWeight: 200,
                                color: 'var(--accent-color)',
                                lineHeight: 1,
                                padding: '20px'
                            }}>
                                {segment.number}
                            </div>

                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: index % 2 === 0 ? '1fr 400px' : '400px 1fr',
                                gap: '60px',
                                position: 'relative',
                                zIndex: 1,
                                alignItems: 'start'
                            }}>
                                {/* Main Content - Order changes based on index */}
                                <div style={{ 
                                    display: 'flex', 
                                    flexDirection: 'column', 
                                    gap: '40px',
                                    order: index % 2 === 0 ? 1 : 2
                                }}>
                                {/* Header */}
                                <div>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '20px',
                                        marginBottom: '20px'
                                    }}>
                                        <div style={{
                                            width: '80px',
                                            height: '80px',
                                            background: 'rgba(201, 169, 110, 0.2)',
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '40px'
                                        }}>
                                            {segment.icon}
                                        </div>
                                        <div>
                                            <div style={{
                                                fontSize: '14px',
                                                letterSpacing: '3px',
                                                textTransform: 'uppercase',
                                                color: 'var(--accent-color)',
                                                marginBottom: '10px',
                                                fontWeight: 500
                                            }}>
                                                Category {segment.number}
                                            </div>
                                            <h2 style={{
                                                ...TYPOGRAPHY.h3,
                                                margin: 0
                                            }}>
                                                {segment.title}
                                            </h2>
                                        </div>
                                    </div>
                                    
                                    <p style={{
                                        ...TYPOGRAPHY.body,
                                        marginBottom: '40px'
                                    }}>
                                        {segment.description}
                                    </p>
                                </div>

                                {/* Items Grid */}
                                <div>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        marginBottom: '25px',
                                        paddingBottom: '15px',
                                        borderBottom: '2px solid var(--accent-color)'
                                    }}>
                                        <h3 style={{
                                            fontSize: '20px',
                                            fontWeight: 400,
                                            color: 'var(--text-light)',
                                            margin: 0
                                        }}>
                                            Specializations
                                        </h3>
                                        <span style={{
                                            fontSize: '16px',
                                            color: 'var(--accent-color)',
                                            fontWeight: 500,
                                            letterSpacing: '1px'
                                        }}>
                                            {segment.items.length} Types
                                        </span>
                                    </div>
                                    
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                                        gap: '15px'
                                    }}>
                                        {segment.items.map((item, idx) => (
                                            <div key={idx} style={{
                                                background: 'rgba(0, 0, 0, 0.3)',
                                                padding: '18px 24px',
                                                borderRadius: '12px',
                                                border: '1px solid rgba(201, 169, 110, 0.15)',
                                                transition: 'all 0.3s ease',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '12px'
                                            }}
                                            className="segment-item-hover"
                                            >
                                                <div style={{
                                                    width: '6px',
                                                    height: '6px',
                                                    background: 'var(--accent-color)',
                                                    borderRadius: '50%',
                                                    flexShrink: 0
                                                }}></div>
                                                <span style={{
                                                    fontSize: '15px',
                                                    color: 'rgba(245, 241, 237, 0.9)',
                                                    lineHeight: 1.5
                                                }}>
                                                    {item}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                </div>

                                {/* Image Section - Order changes based on index */}
                                <div style={{
                                    position: 'sticky',
                                    top: '120px',
                                    height: 'fit-content',
                                    order: index % 2 === 0 ? 2 : 1
                                }}>
                                    <div style={{
                                        borderRadius: '20px',
                                        overflow: 'hidden',
                                        border: '2px solid var(--accent-color)',
                                        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)'
                                    }}>
                                        <img 
                                            src={
                                                index === 0 ? PLACEHOLDER_IMAGES.industrial :
                                                index === 1 ? PLACEHOLDER_IMAGES.healthcare :
                                                index === 2 ? PLACEHOLDER_IMAGES.commercial :
                                                PLACEHOLDER_IMAGES.residential
                                            }
                                            alt={segment.title}
                                            style={{
                                                width: '100%',
                                                height: '500px',
                                                objectFit: 'cover',
                                                display: 'block'
                                            }}
                                        />
                                    </div>
                                    <div style={{
                                        marginTop: '20px',
                                        padding: '20px',
                                        background: 'rgba(201, 169, 110, 0.1)',
                                        borderRadius: '12px',
                                        border: '1px solid rgba(201, 169, 110, 0.2)'
                                    }}>
                                        <div style={{
                                            fontSize: '14px',
                                            color: 'var(--accent-color)',
                                            marginBottom: '8px',
                                            fontWeight: 500,
                                            letterSpacing: '1px'
                                        }}>
                                            {segment.icon} Featured Category
                                        </div>
                                        <div style={{
                                            fontSize: '16px',
                                            color: 'rgba(245, 241, 237, 0.9)',
                                            lineHeight: 1.6
                                        }}>
                                            Comprehensive design solutions tailored for {segment.title.toLowerCase()}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* CTA Section */}
            <section style={{
                background: 'linear-gradient(135deg, #1a1a2e 0%, #0F0F0F 100%)',
                padding: '120px 60px',
                textAlign: 'center',
                borderTop: '2px solid var(--accent-color)'
            }}>
                <div style={{ maxWidth: '900px', margin: '0 auto' }}>
                    <h2 style={{
                        ...TYPOGRAPHY.h2,
                        marginBottom: '25px'
                    }}>
                        Ready to Transform Your Space?
                    </h2>
                    <p style={{
                        ...TYPOGRAPHY.bodyLarge,
                        marginBottom: '50px'
                    }}>
                        No matter what industry segment you're in, we have the expertise and resources
                        to deliver exceptional interior design solutions tailored to your specific needs.
                    </p>
                    <div style={{
                        display: 'flex',
                        gap: '20px',
                        justifyContent: 'center',
                        flexWrap: 'wrap'
                    }}>
                        <Link to="/contact" style={{
                            display: 'inline-block',
                            padding: '18px 45px',
                            background: 'var(--accent-color)',
                            color: '#000',
                            textDecoration: 'none',
                            borderRadius: '12px',
                            fontSize: '16px',
                            fontWeight: 500,
                            letterSpacing: '1px',
                            textTransform: 'uppercase',
                            transition: 'all 0.3s ease',
                            border: '2px solid var(--accent-color)'
                        }}
                        className="cta-btn-primary"
                        >
                            Get Started
                        </Link>
                        <Link to="/studios" style={{
                            display: 'inline-block',
                            padding: '18px 45px',
                            background: 'transparent',
                            color: 'var(--accent-color)',
                            textDecoration: 'none',
                            borderRadius: '12px',
                            fontSize: '16px',
                            fontWeight: 500,
                            letterSpacing: '1px',
                            textTransform: 'uppercase',
                            transition: 'all 0.3s ease',
                            border: '2px solid var(--accent-color)'
                        }}
                        className="cta-btn-secondary"
                        >
                            Explore Studios
                        </Link>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer style={{
                background: '#0A0A0A',
                padding: '40px 60px',
                textAlign: 'center',
                borderTop: '1px solid rgba(201, 169, 110, 0.2)'
            }}>
                <img src={`${process.env.PUBLIC_URL}/current-logo.png`} alt="Hav'n Interiors" style={{
                    height: '50px',
                    marginBottom: '20px',
                    opacity: 0.9
                }} />
                <p style={{
                    color: 'rgba(245, 241, 237, 0.6)',
                    fontSize: '14px',
                    margin: 0
                }}>
                    &copy; 2025 Hav'n Interiors & Global Sourcing. All Rights Reserved.
                </p>
                <p style={{
                    marginTop: '10px',
                    fontSize: '12px',
                    color: 'rgba(245, 241, 237, 0.4)'
                }}>
                    Innovate. Integrate. Execute.
                </p>
            </footer>

            {/* Additional CSS for hover effects and responsive design */}
            <style jsx>{`
                .segment-item-hover:hover {
                    background: rgba(201, 169, 110, 0.15) !important;
                    border-color: var(--accent-color) !important;
                    transform: translateX(5px);
                }

                .cta-btn-primary:hover {
                    background: transparent !important;
                    color: var(--accent-color) !important;
                    transform: translateY(-3px);
                    box-shadow: 0 10px 30px rgba(201, 169, 110, 0.3);
                }

                .cta-btn-secondary:hover {
                    background: var(--accent-color) !important;
                    color: #000 !important;
                    transform: translateY(-3px);
                    box-shadow: 0 10px 30px rgba(201, 169, 110, 0.3);
                }

                @media (max-width: 1024px) {
                    .segment-detail-card > div {
                        grid-template-columns: 1fr !important;
                        gap: 40px !important;
                    }
                }

                @media (max-width: 768px) {
                    .segments-hero {
                        padding: 140px 30px 80px !important;
                    }
                    
                    .segments-stats {
                        padding: 60px 30px !important;
                    }
                    
                    .segment-detail-card {
                        padding: 40px 30px !important;
                    }
                }
            `}</style>
        </div>
    );
};



export default SegmentsPage;

